import React, { useState } from "react";
// La veste è quella delle altre finestrelle di Finance (ManageCategoriesModal,
// ManageContactsModal): stesse classi modalqpsc-*, nessuna nuova.
import "./ManageModalQPSC.css";
// Solo impaginato (larghezze, spazi, stato disabilitato): nessun colore.
import "./ImportTransactionsModal.css";

// La parola che sblocca la scrittura. Il pulsante resta spento finché il
// campo non la contiene ESATTAMENTE (maiuscole comprese).
const PAROLA_DI_CONFERMA = "IMPORT";

/**
 * L'esito quando la scrittura fallisce: il testo da mostrare e se qualcosa
 * PUÒ essere entrato. Il client api.js allega al suo errore `status` (assente
 * se nessuna risposta è mai arrivata) e `body` (il corpo grezzo della
 * risposta). Senza difesa dai doppioni, rilanciare lo stesso file
 * raddoppierebbe le righe già scritte: per questo il messaggio non promette
 * mai ciò che il client non può sapere.
 *
 *   • `written` nella risposta → il server dice quante righe ha scritto
 *     prima di fermarsi: se > 0 va detto chiaro;
 *   • 4xx (o `written: 0`) → il server ha rifiutato PRIMA di scrivere
 *     (vaglio, permessi, rotta assente, corpo troppo grande): si può
 *     correggere e riprovare;
 *   • nessuna risposta, oppure 5xx senza `written` → esito INCERTO: la
 *     connessione può essere caduta dopo il commit, o l'errore può essere
 *     scattato fra un lotto e l'altro. Si dice di controllare la lista.
 *
 * @returns {{ testo: string, puoAverScritto: boolean }}
 */
function esitoDiErrore(err) {
  let corpo = null;
  try {
    corpo = err && err.body ? JSON.parse(err.body) : null;
  } catch {
    corpo = null;
  }
  const status = err && typeof err.status === "number" ? err.status : undefined;
  const scritte = corpo && typeof corpo.written === "number" ? corpo.written : null;

  if (scritte !== null && scritte > 0) {
    return {
      testo: `Import failed after ${scritte} transaction${scritte === 1 ? "" : "s"} had already been written. Close this window and check the transactions list before trying again: importing the same file again would duplicate them.`,
      puoAverScritto: true,
    };
  }
  if (scritte === 0 || (status !== undefined && status < 500)) {
    if (corpo && corpo.error) return { testo: `Import failed: ${corpo.error}`, puoAverScritto: false };
    return { testo: `Import failed (HTTP ${status}). Nothing was imported.`, puoAverScritto: false };
  }
  const causa = status === undefined ? "the server did not reply" : `the server reported an error (HTTP ${status})`;
  return {
    testo: `The import could not be confirmed: ${causa}. Some transactions may already have been saved. Close this window and check the transactions list before importing this file again, to avoid duplicates.`,
    puoAverScritto: true,
  };
}

function formatAmount(val) {
  const number = Number(val);
  if (isNaN(number)) return "";
  return number.toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * ANTEPRIMA E CONFERMA DELL'IMPORT — la finestra che sta fra il file letto e
 * la scrittura. Riceve il vaglio già fatto (utils/importTransactions.js) e
 * NON legge, NON scrive: mostra cosa entrerà e cosa è scartato col motivo, e
 * chiama `onConfirm` con le sole righe valide dopo la conferma digitata.
 *
 * Props:
 *   isOpen     — la finestra si monta solo se vera
 *   fileName   — il nome del file scelto, per l'intestazione
 *   esito      — { errore, valide, scartate } da normalizzaFoglio
 *   onConfirm  — async (transactions) => void; se lancia, l'errore si mostra
 *                qui e la finestra resta aperta
 *   onClose    — chiude senza scrivere
 */
export default function ImportTransactionsModal({ isOpen, fileName, esito, onConfirm, onClose }) {
  const [parola, setParola] = useState("");
  const [sending, setSending] = useState(false);
  const [errore, setErrore] = useState("");

  if (!isOpen || !esito) return null;

  const valide = esito.valide || [];
  const scartate = esito.scartate || [];
  const ignorate = scartate.filter((s) => s.ignorata);
  const respinte = scartate.filter((s) => !s.ignorata);
  const n = valide.length;
  const confermabile = n > 0 && parola === PAROLA_DI_CONFERMA && !sending;

  const handleConfirm = async () => {
    if (!confermabile) return;
    setErrore("");
    setSending(true);
    try {
      await onConfirm(valide.map((v) => v.tx));
    } catch (err) {
      console.error(err);
      const esitoErr = esitoDiErrore(err);
      setErrore(esitoErr.testo);
      // Se qualcosa può essere entrato, la parola digitata si azzera: il
      // pulsante torna spento e un secondo invio richiede di riscrivere
      // IMPORT di proposito, non un click di riflesso.
      if (esitoErr.puoAverScritto) setParola("");
      setSending(false);
    }
  };

  return (
    <div className="modalqpsc-overlay">
      <div className="modalqpsc-window big importtx-window">
        <div className="modalqpsc-header">
          <h3>Import transactions{fileName ? ` — ${fileName}` : ""}</h3>
          <button className="modalqpsc-close" onClick={onClose} disabled={sending} title="Close">
            ✕
          </button>
        </div>
        <div className="modalqpsc-body modalqpsc-scroll">
          {esito.errore && <div className="modalqpsc-error">{esito.errore}</div>}
          {errore && <div className="modalqpsc-error">{errore}</div>}

          {!esito.errore && (
            <>
              <p className="importtx-summary">
                Importing <strong>{n}</strong> transaction{n === 1 ? "" : "s"}.{" "}
                <strong>{respinte.length}</strong> row{respinte.length === 1 ? "" : "s"} skipped
                {ignorate.length > 0 ? ` (plus ${ignorate.length} ignored on purpose)` : ""}.
              </p>

              {n > 0 && (
                <>
                  <h4 className="importtx-heading">Rows to import</h4>
                  <div className="importtx-list">
                    <table className="modalqpsc-table">
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Date</th>
                          <th>Client</th>
                          <th>Category</th>
                          <th>Description</th>
                          <th>Account</th>
                          <th className="importtx-amount">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {valide.map((v) => (
                          <tr key={v.riga}>
                            <td>{v.riga}</td>
                            <td>{v.resa.date}</td>
                            <td>{v.resa.client}</td>
                            <td>{v.resa.category}</td>
                            <td>{v.resa.description}</td>
                            <td>{v.resa.account}</td>
                            <td className="importtx-amount">{formatAmount(v.resa.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {respinte.length > 0 && (
                <>
                  <h4 className="importtx-heading">Skipped rows (not imported)</h4>
                  <div className="importtx-list">
                    <table className="modalqpsc-table">
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {respinte.map((s) => (
                          <tr key={s.riga}>
                            <td>{s.riga}</td>
                            <td>{s.motivo}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {ignorate.length > 0 && (
                <p className="importtx-ignored">
                  Ignored on purpose:{" "}
                  {ignorate.map((s) => `row ${s.riga} (${s.motivo})`).join(", ")}.
                </p>
              )}
            </>
          )}

          <div className="importtx-footer">
            {!esito.errore && n > 0 && (
              <label className="importtx-confirm-label">
                Type {PAROLA_DI_CONFERMA} to confirm
                <input
                  type="text"
                  className="modalqpsc-input importtx-confirm-input"
                  value={parola}
                  onChange={(e) => setParola(e.target.value)}
                  disabled={sending}
                  autoComplete="off"
                  spellCheck={false}
                />
              </label>
            )}
            <div className="importtx-actions">
              <button className="modalqpsc-btn" onClick={onClose} disabled={sending}>
                {n > 0 && !esito.errore && !errore ? "Cancel" : "Close"}
              </button>
              {!esito.errore && n > 0 && (
                <button
                  className="modalqpsc-btn gold importtx-confirm-btn"
                  onClick={handleConfirm}
                  disabled={!confermabile}
                >
                  {sending ? "Importing…" : `Import ${n} transaction${n === 1 ? "" : "s"}`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
